/**
 * Écran de connexion — étape 1, point 7.
 *
 * Conforme à la maquette validée le 02/09/2026 : e-mail et mot de passe, pas de
 * « mot de passe oublié », pas de « créer un compte », pas de sélecteur de
 * société, aucune phrase en pied d'écran.
 *
 * Se connecter est **deux opérations** : Supabase vérifie les identifiants et
 * rend un jeton, puis l'API dit si l'accès est ouvert. Les deux peuvent échouer
 * séparément, et l'écran doit le dire différemment — c'est tout l'objet du
 * tableau `MESSAGES` ci-dessous.
 */

import { useId, useState, type FormEvent } from 'react';
import { lireMoi, type EchecAcces, type Moi } from './api.js';
import { seConnecter, type EchecConnexion } from './authentification.js';

type Refus = EchecConnexion | EchecAcces;

/**
 * Messages tels que validés sur la maquette. Volontairement courts, **sauf les
 * deux où la personne peut agir seule** : attendre, ou vérifier sa connexion.
 * Les autres exigent quelqu'un d'autre ; les allonger n'aiderait personne.
 */
const MESSAGES: Readonly<Record<Refus, string>> = {
  // Un seul message pour « e-mail inconnu » et « mot de passe faux » : en
  // distinguer deux permettrait de deviner de l'extérieur qui a un compte.
  identifiants: 'E-mail ou mot de passe incorrect.',
  non_confirme: 'Compte non confirmé.',
  trop_de_tentatives: 'Trop de tentatives. Réessayez dans quelques minutes.',
  ferme: "Votre accès n'est pas ouvert.",
  desactive: 'Votre compte est désactivé.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
};

/** Les refus sur lesquels réessayer tout de suite n'a aucun sens. */
const SANS_NOUVEL_ESSAI: ReadonlySet<Refus> = new Set<Refus>(['trop_de_tentatives']);

interface Proprietes {
  readonly quandConnecte: (moi: Moi) => void;
}

export function EcranConnexion({ quandConnecte }: Proprietes) {
  const idEmail = useId();
  const idMotDePasse = useId();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [motDePasseVisible, setMotDePasseVisible] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState<Refus | null>(null);

  async function envoyer(evenement: FormEvent) {
    evenement.preventDefault();
    if (enCours) {
      return;
    }
    setEnCours(true);
    setRefus(null);

    // Étape 1 — Supabase : qui êtes-vous ?
    const connexion = await seConnecter(email, motDePasse);
    if (connexion.etat === 'echec') {
      setRefus(connexion.cause);
      setEnCours(false);
      return;
    }

    // Étape 2 — l'API : avez-vous le droit d'entrer ?
    const acces = await lireMoi(connexion.session.access_token);
    if (acces.etat === 'echec') {
      setRefus(acces.cause);
      setEnCours(false);
      return;
    }

    setEnCours(false);
    quandConnecte(acces.moi);
  }

  const fautif = refus === 'identifiants';
  const bloque = enCours || (refus !== null && SANS_NOUVEL_ESSAI.has(refus));

  return (
    <main className="scene">
      <form className="ecran" onSubmit={envoyer} noValidate>
        <div className="marque">
          <span className="mot">Stafa</span>
          <span className="metier">Gestion de transport</span>
        </div>

        {refus !== null && (
          <p
            className={`bandeau ${fautif || refus === 'ferme' || refus === 'desactive' ? 'bandeau-refus' : 'bandeau-attente'}`}
            role="alert"
          >
            {MESSAGES[refus]}
          </p>
        )}

        <div className="champs">
          <div className="bloc-champ">
            <label className="etiquette" htmlFor={idEmail}>
              Adresse e-mail
            </label>
            <input
              className={`champ${fautif ? ' fautif' : ''}`}
              id={idEmail}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              disabled={enCours}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom.nom@societe.fr"
            />
          </div>

          <div className="bloc-champ">
            <label className="etiquette" htmlFor={idMotDePasse}>
              Mot de passe
            </label>
            <div className="champ-mdp">
              <input
                className={`champ${fautif ? ' fautif' : ''}`}
                id={idMotDePasse}
                type={motDePasseVisible ? 'text' : 'password'}
                name="motDePasse"
                autoComplete="current-password"
                required
                disabled={enCours}
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
              />
              {/* Un quai, un téléphone, un gant : on tape mal. Voir ce qu'on a
                  saisi évite la moitié des échecs. */}
              <button
                className="voir"
                type="button"
                disabled={enCours}
                onClick={() => setMotDePasseVisible((visible) => !visible)}
              >
                {motDePasseVisible ? 'Masquer' : 'Afficher'}
              </button>
            </div>
          </div>
        </div>

        <button className="bouton" type="submit" disabled={bloque}>
          {enCours ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </main>
  );
}
